import { i18n } from 'i18n';
import {
  CustomUnitFields,
  DisplayUnits,
  RheologyDataDto,
  UnitSystem
} from 'providers/api';
import { isNumber } from 'utils';
import { calculateYieldfromConcentrationByMass } from 'utils/backfill';
import {
  distanceMetricToImperial,
  flowRateMetricToImperial,
  isCustomUnitField,
  pressureMetricToImperial
} from 'utils/unitConversions';
import { additionalUnitLabels, BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { convertkPaToM } from '../canvas/Model/Telemetry/conversions';
import { isChokeNode } from '../MineModel/Nodes';
import PCRoute from '../MineModel/Route/PCRoute';
import { RecipeState, RoutePoint } from '../MineModel/types';
import * as GConstants from '../MineModel/GConstants';
import { HglPoint } from './hglCsvExport';
import { HglImageContext, HglImageMarkerGroup, HglImageSeries } from './hglImageExport';

// Narrower than the live chart on purpose: no flush overlay, optimiser target lines or secondary
// pressure axis, none of which is available off the stopes page.

export interface RouteHglImageParams {
  route: PCRoute;
  recipeState: RecipeState;
  rheologyDataset?: RheologyDataDto;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
  formatNumber: (value: number, decimals: number) => string;
  slackFlow?: PressurePass;
  maxPressure?: PressurePass;
  pipeRatingOffsetPercent?: number;
  stopeName?: string;
}

export interface PressurePass {
  pressuresByPointId: Record<string, number>;
  slurryDensity: number;
}

const DEFAULT_SLURRY_DENSITY = 1800;

const hasPoints = (points: HglPoint[]) => points.length > 0;

const makeConverters = (isImperial: boolean) => {
  const toDistance = (value: number) => (isImperial ? distanceMetricToImperial(value) : value);
  return {
    toDistance,
    hglHeight: (pressureKpa: number | undefined, elevation: number, density: number) => toDistance(convertkPaToM(pressureKpa, density)) + toDistance(elevation),
  };
};

const pressureSeriesFrom = (
  routePoints: RoutePoint[],
  pressureOf: (point: RoutePoint) => number | undefined,
  density: number,
  isImperial: boolean,
): HglPoint[] => {
  const { toDistance, hglHeight } = makeConverters(isImperial);
  return routePoints.map((point) => ({
    x: toDistance(point.distanceFromPump ?? 0),
    y: hglHeight(pressureOf(point), point.point.coords.z, density),
  }));
};

const toPercent = (concentration?: number) => {
  if (!isNumber(concentration) || concentration <= 0) return undefined;
  return concentration > 1 ? concentration : concentration * 100;
};

const buildSummary = (
  { recipeState, rheologyDataset, stopeName }: RouteHglImageParams,
  pumpPressureKpa: number | undefined,
  isImperialProject: boolean,
  isCustomPressureUnit: boolean,
) => {
  const massConcentrationPercent = toPercent(recipeState?.totalDryConcentrationByMass);
  const coefficients = rheologyDataset?.yieldStressCoefficients;
  const flowRateUnit = additionalUnitLabels[DisplayUnits.FlowRate];
  const wetFlowRate = recipeState?.wetFlowRate;
  const toDisplayPressure = (kpa: number) => (isCustomPressureUnit ? pressureMetricToImperial(kpa) : kpa);

  return {
    stopeName,
    pumpPressure: isNumber(pumpPressureKpa) ? toDisplayPressure(pumpPressureKpa) : undefined,
    yieldStressPa: coefficients && isNumber(massConcentrationPercent)
      ? calculateYieldfromConcentrationByMass(massConcentrationPercent, coefficients.coefficient1, coefficients.coefficient2)
      : undefined,
    massConcentrationPercent,
    flowRate: isNumber(wetFlowRate) && isImperialProject ? flowRateMetricToImperial(wetFlowRate) : wetFlowRate,
    flowRateAbbr: isImperialProject ? flowRateUnit.imperial.abbreviation : flowRateUnit.metric.abbreviation,
  };
};

const buildRouteHglImageContext = (params: RouteHglImageParams): HglImageContext => {
  const {
    route, recipeState, unitSystemPreference, customUnitFields, formatNumber,
    slackFlow, maxPressure, pipeRatingOffsetPercent,
  } = params;

  const isImperial = unitSystemPreference === UnitSystem.Imperial;
  const { toDistance, hglHeight } = makeConverters(isImperial);
  const slurryDensity = recipeState?.wetDensity ?? DEFAULT_SLURRY_DENSITY;
  const routePoints = route.routePoints ?? [];

  const elevationData: HglPoint[] = [];
  const pressureData: HglPoint[] = [];
  const pipeRatingData: HglPoint[] = [];
  const ruptureDiskData: HglPoint[] = [];
  const chokeStationData: HglPoint[] = [];

  routePoints.forEach((point) => {
    const elevation = point.point.coords.z;
    const distance = toDistance(point.distanceFromPump ?? 0);
    const elevationConverted = toDistance(elevation);

    elevationData.push({ x: distance, y: elevationConverted });
    pressureData.push({ x: distance, y: hglHeight(point.pressure, elevation, slurryDensity) });

    if ((point.point.ruptureDiskPressure ?? 0) > 0) {
      ruptureDiskData.push({ x: distance, y: hglHeight(point.point.ruptureDiskPressure, elevation, slurryDensity) });
    }

    if (isChokeNode(point.point.id) && point.point.chokeHead > 0) {
      chokeStationData.push({ x: distance, y: hglHeight(point.pressure, elevation, slurryDensity) });
    }

    if (point.pipeDownstream) {
      pipeRatingData.push({ x: distance, y: hglHeight(point.pipeDownstream.pressureRating, elevation, slurryDensity) });
    }
    if (point.pipeUpstream) {
      pipeRatingData.push({ x: distance, y: hglHeight(point.pipeUpstream.pressureRating, elevation, slurryDensity) });
    }
  });

  const pumpPressureKpa = route.pumpPressureKpa || recipeState?.pumpPressureKpa;
  const showTransient = isNumber(pipeRatingOffsetPercent) && pipeRatingOffsetPercent > 0 && isNumber(pumpPressureKpa);
  const transientOffset = showTransient
    ? toDistance(convertkPaToM(pumpPressureKpa, slurryDensity) * ((pipeRatingOffsetPercent as number) / 100))
    : 0;
  const transientData: HglPoint[] = showTransient
    ? pressureData.map(({ x, y }) => ({ x, y: y + transientOffset }))
    : [];

  const slackFlowData = slackFlow
    ? pressureSeriesFrom(routePoints, (point) => slackFlow.pressuresByPointId[point.point.id], slackFlow.slurryDensity, isImperial)
    : [];
  const maxPressureData = maxPressure
    ? pressureSeriesFrom(routePoints, (point) => maxPressure.pressuresByPointId[point.point.id], maxPressure.slurryDensity, isImperial)
    : [];

  const series: HglImageSeries[] = [
    { name: i18n._('Pipe Profile'), data: elevationData, color: GConstants.GREEN },
    { name: i18n._('Pipe Rating'), data: pipeRatingData, color: GConstants.RED },
    { name: i18n._('Max Pressure'), data: maxPressureData, color: GConstants.DARK_RED },
    { name: i18n._('HGL'), data: pressureData, color: GConstants.WHITE },
    { name: i18n._('Slack Flow'), data: slackFlowData, color: GConstants.CYAN },
    { name: i18n._('Transient'), data: transientData, color: GConstants.ORANGE },
  ].filter(({ data }) => hasPoints(data));

  const markerGroups: HglImageMarkerGroup[] = [
    { name: i18n._('Rupture Disk'), color: GConstants.LIGHT_BLUE, data: ruptureDiskData, lines: [] },
    { name: i18n._('Choke Station'), color: GConstants.ORANGE, data: chokeStationData, lines: [] },
  ].filter(({ data }) => hasPoints(data));

  const distanceUnit = unitLabels[BasicDisplayUnits.Distance];
  const pressureUnit = unitLabels[BasicDisplayUnits.PressureUCS];
  const isCustomPressureUnit = isCustomUnitField(unitSystemPreference, customUnitFields, pressureUnit.customUnitField);

  return {
    series,
    markerGroups,
    summary: buildSummary(params, pumpPressureKpa, isImperial, isCustomPressureUnit),
    distanceAbbr: isImperial ? distanceUnit.imperial.abbreviation : distanceUnit.metric.abbreviation,
    pressureSeriesName: i18n._('Pressure'),
    pressureAbbr: isCustomPressureUnit ? pressureUnit.imperial.abbreviation : pressureUnit.metric.abbreviation,
    dashedSeriesNames: [i18n._('Transient')],
    formatNumber,
  };
};

export default buildRouteHglImageContext;
